import React, { useEffect, useState } from 'react';
import AutoComplete from '../CustomSelect/AutoComplete';
import styled from 'styled-components';
import { AxiosError, AxiosResponse } from 'axios';
import { PartOptions } from '@src/pages/Products';
import { ApplicationContext } from '@src/contexts/ApplicationContext';
import { Filters, Product } from '@src/interfaces/type';
import CustomTextButton from '../CustomButton/CustomTextButton';
import { Box, Collapse, ListItemText, Typography } from '@mui/material';
import { useAppDispatch } from '@src/store/hooks';
import { setPageNumber } from '@src/store/reducers/catalogReducer';
import { IsMobileWidth } from '../MediaQueryUtils';

const StyledContainer = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 10px;
	align-items: center;
	padding: 8px;
	width: 100%;
`;

const SeeMoreButton = styled.button`
	margin-top: 16px;
	align-self: flex-end;
`;

type Option = {
	id: string;
	value: string;
};

interface Props {
	filters: Filters | null;
	setFilters: (data: Filters) => void;
	activeFilter?: string;
	setActiveFilter?: (data: string) => void;
	products?: Product[];
	setFilteredProducts?: any;
	filterType?: 'client' | 'server';
	setLoading?: any;
	setPage?: any;
}

const CustomFilter: React.FC<Props> = ({
	filters,
	setFilters,
	activeFilter,
	setActiveFilter,
	products,
	setFilteredProducts,
	filterType = 'client',
	setLoading,
	setPage,
}) => {
	const { apiService }: any = React.useContext(ApplicationContext);
	const dispatch = useAppDispatch();
	const isMobile = IsMobileWidth();

	const [partOptions, setPartOptions] = useState<PartOptions | null>(null);
	const [lastFilteredProducts, setLastFilteredProducts] = useState<Product[]>([]);
	const [showAllFilters, setShowAllFilters] = useState<boolean>(false);

	const filterProducts = (products: Product[], filters: Partial<Filters>) => {
		return products.filter((product) => {
			return Object.entries(filters).every(([key, filter]) => {
				if (!filter) return true;
				const productValue = product[key as keyof Product];
				//@ts-ignore
				return productValue && String(productValue) === String(filter?.id);
			});
		});
	};

	const handlePageChange = () => {
		let value = 1;
		setPage(value);
		dispatch(setPageNumber(value));

		const searchParams = new URLSearchParams();
		searchParams.set('page', value?.toString());
		searchParams.set('limit', '25');

		//update URL with new params values
		const newUrl = new URL(window.location.href);
		newUrl.search = searchParams.toString();
		window.history.replaceState({}, '', newUrl.toString());
	};

	useEffect(() => {
		const geMachineBrandsPromise: Promise<any> = new Promise((resolve, reject) => {
			apiService.getManufacturers(
				(response: AxiosResponse) => {
					const options = response?.data?.data?.map((manufacturer: any) => {
						return { value: manufacturer?.name, id: manufacturer?.machine_manufacturer_id };
					});
					resolve(options);
				},
				(error: AxiosError) => reject(error)
			);
		});

		const getModelsPromise: Promise<any> = new Promise((resolve, reject) => {
			apiService.getModels(
				(response: AxiosResponse) => {
					const options = response?.data?.data?.map((model: any) => {
						return { value: model?.name, id: model?.model_id };
					});
					resolve(options);
				},
				(error: AxiosError) => reject(error)
			);
		});

		const getDimensionsPromise: Promise<any> = new Promise((resolve, reject) => {
			apiService.getDimensions(
				(response: AxiosResponse) => {
					const options = response?.data?.data?.map((dimension: any) => {
						return { value: dimension?.name, id: dimension?.part_dimension_id };
					});
					resolve(options);
				},
				(error: AxiosError) => reject(error)
			);
		});

		const getDesignsPromise: Promise<any> = new Promise((resolve, reject) => {
			apiService.getDesigns(
				(response: AxiosResponse) => {
					const options = response?.data?.data?.map((design: any) => {
						return { value: design?.name, id: design?.part_design_id };
					});
					resolve(options);
				},
				(error: AxiosError) => reject(error)
			);
		});

		const getCompositionsPromise: Promise<any> = new Promise((resolve, reject) => {
			apiService.getCompositions(
				(response: AxiosResponse) => {
					const options = response?.data?.data?.map((composition: any) => {
						return { value: composition?.name, id: composition?.part_composition_id };
					});
					resolve(options);
				},
				(error: AxiosError) => reject(error)
			);
		});

		const getWeightsPromise: Promise<any> = new Promise((resolve, reject) => {
			apiService.getWeights(
				(response: AxiosResponse) => {
					const options = response?.data?.data?.map((weight: any) => {
						return { value: weight?.name, id: weight?.part_weight_id };
					});
					resolve(options);
				},
				(error: AxiosError) => reject(error)
			);
		});

		const subCategoryPromise = new Promise<any>((resolve, reject) => {
			apiService?.getSubCategories(
				(response: AxiosResponse) => {
					const options = response?.data?.data?.map((option: any) => {
						return {
							value: option?.name,
							id: option?.sub_category_id,
							category_name: option?.category_name,
						};
					});
					resolve(options);
				},
				(error: AxiosError) => reject(error)
			);
		});

		const getBrandsPromise: Promise<any> = new Promise((resolve, reject) => {
			apiService.getBrands(
				(response: AxiosResponse) => {
					const options = response?.data?.data?.map((brand: any) => {
						return { value: brand?.name, id: brand?.brand_id };
					});
					resolve(options);
				},
				(error: AxiosError) => reject(error)
			);
		});

		// const getGroupsPromise: Promise<any> = new Promise((resolve, reject) => {
		// 	apiService.getGroups(
		// 		(response: AxiosResponse) => {
		// 			const options = response?.data?.data?.map((group: any) => {
		// 				return { value: group?.name, id: group?.group_category_id };
		// 			});
		// 			resolve(options);
		// 		},
		// 		(error: AxiosError) => reject(error)
		// 	);
		// });

		// const categoryPromise: Promise<any> = new Promise((resolve, reject) => {
		// 	apiService?.getCategories(
		// 		(response: AxiosResponse) => {
		// 			const options = response?.data?.data?.map((option: any) => {
		// 				return { value: option?.name, id: option?.catgeory_id };
		// 			});
		// 			resolve(options);
		// 		},
		// 		(error: AxiosError) => reject(error)
		// 	);
		// });

		Promise.all([
			geMachineBrandsPromise,
			getModelsPromise,
			getDimensionsPromise,
			getDesignsPromise,
			getCompositionsPromise,
			getWeightsPromise,
			subCategoryPromise,
			getBrandsPromise,
			// getGroupsPromise,
			// categoryPromise,
		])
			.then(
				([
					machineBrandOptions,
					modelOptions,
					dimensionOptions,
					designOptions,
					compositionOptions,
					weightOptions,
					subCategoryOptions,
					brandOptions,
					// groupOptions,
					// categoryOptions,
				]) => {
					const data = {
						machineBrandOptions,
						modelOptions,
						dimensionOptions,
						designOptions,
						compositionOptions,
						weightOptions,
						subCategoryOptions,
						brandOptions,
						// groupOptions,
						// categoryOptions,
					};

					setPartOptions(data);
				}
			)
			.catch((error: AxiosError) => {
				console.error(error);
				// displayError('Something went wrong');
			});
	}, []);

	if (partOptions) {
		if (filterType === 'client' && setFilteredProducts && products) {
			if (isMobile) {
				return (
					<div style={{ display: 'flex', flexDirection: 'column' }}>
						<StyledContainer>
							<Collapse in={showAllFilters} style={{ width: '100%' }}>
								<div
									style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}
								>
									<div style={{ width: isMobile ? '100%' : '160px' }}>
										<AutoComplete
											//@ts-ignore
											selectedOption={filters?.machineModelId}
											options={partOptions?.modelOptions || []}
											label="Machine Model"
											onChange={(newOption: Option | null) => {
												//@ts-ignore
												const updatedFilters: Filters = { ...filters, machineModelId: newOption };

												const filteredProducts = filterProducts(products, updatedFilters);

												setFilteredProducts(filteredProducts);
												setFilters(updatedFilters);
											}}
											placeholder="Select a model"
										/>
									</div>
									<div style={{ width: isMobile ? '100%' : '200px' }}>
										<AutoComplete
											//@ts-ignore
											selectedOption={filters?.subCategoryId}
											options={partOptions?.subCategoryOptions || []}
											label="Sub-Cat"
											onChange={(newOption: Option | null) => {
												//@ts-ignore
												const updatedFilters: Filters = { ...filters, subCategoryId: newOption };

												const filteredProducts = filterProducts(products, updatedFilters);

												setFilteredProducts(filteredProducts);
												setFilters(updatedFilters);
											}}
											placeholder="Select a sub category"
											renderOption={(props: any, item: any) => {
												return (
													<Box
														{...props}
														key={item?.id}
														display={'flex'}
														flexDirection={'column'}
														sx={{
															alignItems: 'flex-start !important',
														}}
													>
														<ListItemText>{item?.value}</ListItemText>
														<Typography sx={{ fontSize: '10px', color: 'gray' }}>
															{item?.category_name}
														</Typography>
													</Box>
												);
											}}
										/>
									</div>
									<div style={{ width: isMobile ? '100%' : '160px' }}>
										<AutoComplete
											//@ts-ignore
											selectedOption={filters?.brandId}
											options={partOptions?.brandOptions || []}
											label="Brand"
											onChange={(newOption: Option | null) => {
												//@ts-ignore
												const updatedFilters: Filters = { ...filters, brandId: newOption };

												const filteredProducts = filterProducts(products, updatedFilters);

												setFilteredProducts(filteredProducts);
												setFilters(updatedFilters);
											}}
											placeholder="Select a brand"
										/>
									</div>

									<div style={{ width: isMobile ? '100%' : '200px' }}>
										<AutoComplete
											//@ts-ignore
											selectedOption={filters?.partDimensionId}
											options={partOptions?.dimensionOptions || []}
											label="Dimension"
											onChange={(newOption: Option | null) => {
												//@ts-ignore
												const updatedFilters: Filters = { ...filters, partDimensionId: newOption };

												const filteredProducts = filterProducts(products, updatedFilters);

												setFilteredProducts(filteredProducts);
												setFilters(updatedFilters);
											}}
											placeholder="Select a dimension"
										/>
									</div>
									<div style={{ width: isMobile ? '100%' : '200px' }}>
										<AutoComplete
											//@ts-ignore
											selectedOption={filters?.partDesignId}
											options={partOptions?.designOptions || []}
											label="Design"
											onChange={(newOption: Option | null) => {
												//@ts-ignore
												const updatedFilters: Filters = { ...filters, partDesignId: newOption };

												const filteredProducts = filterProducts(products, updatedFilters);

												setFilteredProducts(filteredProducts);
												setFilters(updatedFilters);
											}}
											placeholder="Select a design"
										/>
									</div>
									<div style={{ width: isMobile ? '100%' : '200px' }}>
										<AutoComplete
											//@ts-ignore
											selectedOption={filters?.partWeightId}
											options={partOptions?.weightOptions || []}
											label="Weight"
											onChange={(newOption: Option | null) => {
												//@ts-ignore
												const updatedFilters: Filters = { ...filters, partWeightId: newOption };

												const filteredProducts = filterProducts(products, updatedFilters);

												setFilteredProducts(filteredProducts);
												setFilters(updatedFilters);
											}}
											placeholder="Select a weight"
										/>
									</div>
									<div style={{ width: isMobile ? '100%' : '200px' }}>
										<AutoComplete
											//@ts-ignore
											selectedOption={filters?.partCompositionId}
											onChange={(newOption: Option | null) => {
												//@ts-ignore
												const updatedFilters: Filters = {
													...filters,
													partCompositionId: newOption,
												};

												const filteredProducts = filterProducts(products, updatedFilters);

												setFilteredProducts(filteredProducts);
												setFilters(updatedFilters);
											}}
											options={partOptions?.compositionOptions || []}
											label="Composition"
											placeholder="Select a composition"
										/>
									</div>
									<div style={{ width: isMobile ? '100%' : '160px' }}>
										<AutoComplete
											//@ts-ignore
											selectedOption={filters?.machineBrandId}
											options={partOptions?.machineBrandOptions || []}
											label="Machine Brand"
											onChange={(newOption: Option | null) => {
												//@ts-ignore
												const updatedFilters: Filters = { ...filters, machineBrandId: newOption };

												const filteredProducts = filterProducts(products, updatedFilters);

												setFilteredProducts(filteredProducts);
												setFilters(updatedFilters);
											}}
											placeholder="Select a Machine brand"
										/>
									</div>
								</div>
							</Collapse>

							{/* Toggle additional filters */}
						</StyledContainer>
						<div
							style={{
								display: 'flex',
								alignItems: 'flex-end',
								justifyContent: 'flex-end',
								marginRight: '16px',
								marginBottom: '10px',
							}}
						>
							<CustomTextButton
								title={showAllFilters ? 'Show fewer filters' : 'Show more filters'}
								onClick={() => setShowAllFilters(!showAllFilters)}
							/>
						</div>
					</div>
				);
			}

			return (
				<div style={{ display: 'flex', flexDirection: 'column' }}>
					<StyledContainer>
						<div style={{ width: isMobile ? '100%' : '160px' }}>
							<AutoComplete
								//@ts-ignore
								selectedOption={filters?.machineModelId}
								options={partOptions?.modelOptions || []}
								label="Machine Model"
								onChange={(newOption: Option | null) => {
									//@ts-ignore
									const updatedFilters: Filters = { ...filters, machineModelId: newOption };

									const filteredProducts = filterProducts(products, updatedFilters);

									setFilteredProducts(filteredProducts);
									setFilters(updatedFilters);
								}}
								placeholder="Select a model"
							/>
						</div>
						<div style={{ width: isMobile ? '100%' : '200px' }}>
							<AutoComplete
								//@ts-ignore
								selectedOption={filters?.subCategoryId}
								options={partOptions?.subCategoryOptions || []}
								label="Sub-Cat"
								onChange={(newOption: Option | null) => {
									//@ts-ignore
									const updatedFilters: Filters = { ...filters, subCategoryId: newOption };

									const filteredProducts = filterProducts(products, updatedFilters);

									setFilteredProducts(filteredProducts);
									setFilters(updatedFilters);
								}}
								placeholder="Select a sub category"
								renderOption={(props: any, item: any) => {
									return (
										<Box
											{...props}
											key={item?.id}
											display={'flex'}
											flexDirection={'column'}
											sx={{
												alignItems: 'flex-start !important',
											}}
										>
											<ListItemText>{item?.value}</ListItemText>
											<Typography sx={{ fontSize: '10px', color: 'gray' }}>
												{item?.category_name}
											</Typography>
										</Box>
									);
								}}
							/>
						</div>
						<div style={{ width: isMobile ? '100%' : '160px' }}>
							<AutoComplete
								//@ts-ignore
								selectedOption={filters?.brandId}
								options={partOptions?.brandOptions || []}
								label="Brand"
								onChange={(newOption: Option | null) => {
									//@ts-ignore
									const updatedFilters: Filters = { ...filters, brandId: newOption };

									const filteredProducts = filterProducts(products, updatedFilters);

									setFilteredProducts(filteredProducts);
									setFilters(updatedFilters);
								}}
								placeholder="Select a brand"
							/>
						</div>

						<div style={{ width: isMobile ? '100%' : '200px' }}>
							<AutoComplete
								//@ts-ignore
								selectedOption={filters?.partDimensionId}
								options={partOptions?.dimensionOptions || []}
								label="Dimension"
								onChange={(newOption: Option | null) => {
									//@ts-ignore
									const updatedFilters: Filters = { ...filters, partDimensionId: newOption };

									const filteredProducts = filterProducts(products, updatedFilters);

									setFilteredProducts(filteredProducts);
									setFilters(updatedFilters);
								}}
								placeholder="Select a dimension"
							/>
						</div>
						<div style={{ width: isMobile ? '100%' : '200px' }}>
							<AutoComplete
								//@ts-ignore
								selectedOption={filters?.partDesignId}
								options={partOptions?.designOptions || []}
								label="Design"
								onChange={(newOption: Option | null) => {
									//@ts-ignore
									const updatedFilters: Filters = { ...filters, partDesignId: newOption };

									const filteredProducts = filterProducts(products, updatedFilters);

									setFilteredProducts(filteredProducts);
									setFilters(updatedFilters);
								}}
								placeholder="Select a design"
							/>
						</div>
						<Collapse in={showAllFilters} style={{ width: '100%' }}>
							<div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
								<div style={{ width: isMobile ? '100%' : '200px' }}>
									<AutoComplete
										//@ts-ignore
										selectedOption={filters?.partWeightId}
										options={partOptions?.weightOptions || []}
										label="Weight"
										onChange={(newOption: Option | null) => {
											//@ts-ignore
											const updatedFilters: Filters = { ...filters, partWeightId: newOption };

											const filteredProducts = filterProducts(products, updatedFilters);

											setFilteredProducts(filteredProducts);
											setFilters(updatedFilters);
										}}
										placeholder="Select a weight"
									/>
								</div>
								<div style={{ width: isMobile ? '100%' : '200px' }}>
									<AutoComplete
										//@ts-ignore
										selectedOption={filters?.partCompositionId}
										onChange={(newOption: Option | null) => {
											//@ts-ignore
											const updatedFilters: Filters = { ...filters, partCompositionId: newOption };

											const filteredProducts = filterProducts(products, updatedFilters);

											setFilteredProducts(filteredProducts);
											setFilters(updatedFilters);
										}}
										options={partOptions?.compositionOptions || []}
										label="Composition"
										placeholder="Select a composition"
									/>
								</div>
								<div style={{ width: isMobile ? '100%' : '160px' }}>
									<AutoComplete
										//@ts-ignore
										selectedOption={filters?.machineBrandId}
										options={partOptions?.machineBrandOptions || []}
										label="Machine Brand"
										onChange={(newOption: Option | null) => {
											//@ts-ignore
											const updatedFilters: Filters = { ...filters, machineBrandId: newOption };

											const filteredProducts = filterProducts(products, updatedFilters);

											setFilteredProducts(filteredProducts);
											setFilters(updatedFilters);
										}}
										placeholder="Select a Machine brand"
									/>
								</div>
							</div>
						</Collapse>

						{/* Toggle additional filters */}
					</StyledContainer>
					<div
						style={{
							display: 'flex',
							alignItems: 'flex-end',
							justifyContent: 'flex-end',
							marginRight: '16px',
							marginBottom: '10px',
						}}
					>
						<CustomTextButton
							title={showAllFilters ? 'Show fewer filters' : 'Show more filters'}
							onClick={() => setShowAllFilters(!showAllFilters)}
						/>
					</div>
				</div>
			);
		}

		if (isMobile) {
			return (
				<div style={{ display: 'flex', flexDirection: 'column' }}>
					<StyledContainer>
						<Collapse in={showAllFilters} style={{ width: '100%' }}>
							<div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
								<div style={{ width: isMobile ? '100%' : '160px' }}>
									<AutoComplete
										//@ts-ignore
										selectedOption={filters?.model}
										options={partOptions?.modelOptions || []}
										label="Machine Model"
										onChange={(newOption: Option | null) => {
											setLoading(true);
											//@ts-ignore
											setFilters({ ...filters, model: newOption });
											handlePageChange();
										}}
										placeholder="Select a model"
									/>
								</div>

								<div style={{ width: isMobile ? '100%' : '200px' }}>
									<AutoComplete
										//@ts-ignore
										selectedOption={filters?.subCategory}
										options={partOptions?.subCategoryOptions || []}
										label="Sub-Cat"
										onChange={(newOption: Option | null) => {
											setLoading(true);
											//@ts-ignore
											setFilters({ ...filters, subCategory: newOption });
											handlePageChange();
										}}
										placeholder="Select a sub category"
										renderOption={(props: any, item: any) => {
											return (
												<Box
													{...props}
													key={item?.id}
													display={'flex'}
													flexDirection={'column'}
													sx={{
														alignItems: 'flex-start !important',
													}}
												>
													<ListItemText>{item?.value}</ListItemText>
													<Typography sx={{ fontSize: '10px', color: 'gray' }}>
														{item?.category_name}
													</Typography>
												</Box>
											);
										}}
									/>
								</div>
								<div style={{ width: isMobile ? '100%' : '160px' }}>
									<AutoComplete
										//@ts-ignore
										selectedOption={filters?.brand}
										options={partOptions?.brandOptions || []}
										label="Brand"
										onChange={(newOption: Option | null) => {
											setLoading(true);
											//@ts-ignore
											setFilters({ ...filters, brand: newOption });
											handlePageChange();
										}}
										placeholder="Select a brand"
									/>
								</div>
								<div style={{ width: isMobile ? '100%' : '200px' }}>
									<AutoComplete
										//@ts-ignore
										selectedOption={filters?.dimension}
										options={partOptions?.dimensionOptions || []}
										label="Dimension"
										onChange={(newOption: Option | null) => {
											setLoading(true);
											//@ts-ignore
											setFilters({ ...filters, dimension: newOption });
										}}
										placeholder="Select a dimension"
									/>
								</div>
								<div style={{ width: isMobile ? '100%' : '200px' }}>
									<AutoComplete
										//@ts-ignore
										selectedOption={filters?.design}
										options={partOptions?.designOptions || []}
										label="Design"
										onChange={(newOption: Option | null) => {
											setLoading(true);
											//@ts-ignore
											setFilters({ ...filters, design: newOption });
										}}
										placeholder="Select a design"
									/>
								</div>
								<div style={{ width: isMobile ? '100%' : '200px' }}>
									<AutoComplete
										//@ts-ignore
										selectedOption={filters?.weight}
										options={partOptions?.weightOptions || []}
										label="Weight"
										onChange={(newOption: Option | null) => {
											setLoading(true);
											//@ts-ignore
											setFilters({ ...filters, weight: newOption });
											handlePageChange();
										}}
										placeholder="Select a weight"
									/>
								</div>
								<div style={{ width: isMobile ? '100%' : '200px' }}>
									<AutoComplete
										//@ts-ignore
										selectedOption={filters?.composition}
										onChange={(newOption: Option | null) => {
											setLoading(true);
											//@ts-ignore
											setFilters({ ...filters, composition: newOption });
											handlePageChange();
										}}
										options={partOptions?.compositionOptions || []}
										label="Composition"
										placeholder="Select a composition"
									/>
								</div>
								<div style={{ width: isMobile ? '100%' : '160px' }}>
									<AutoComplete
										//@ts-ignore
										selectedOption={filters?.machineBrand}
										options={partOptions?.machineBrandOptions || []}
										label="Machin Brand"
										onChange={(newOption: Option | null) => {
											setLoading(true);
											//@ts-ignore
											setFilters({ ...filters, machineBrand: newOption });
											handlePageChange();
										}}
										placeholder="Select a Machine brand"
									/>
								</div>
							</div>
						</Collapse>
					</StyledContainer>
					<div
						style={{
							display: 'flex',
							alignItems: 'flex-end',
							justifyContent: 'flex-end',
							marginRight: '16px',
							marginBottom: '10px',
						}}
					>
						<CustomTextButton
							title={showAllFilters ? 'Show fewer filters' : 'Show more filters'}
							onClick={() => setShowAllFilters(!showAllFilters)}
						/>
					</div>
				</div>
			);
		}

		return (
			<div style={{ display: 'flex', flexDirection: 'column' }}>
				<StyledContainer>
					<div style={{ width: isMobile ? '100%' : '160px' }}>
						<AutoComplete
							//@ts-ignore
							selectedOption={filters?.model}
							options={partOptions?.modelOptions || []}
							label="Machine Model"
							onChange={(newOption: Option | null) => {
								setLoading(true);
								//@ts-ignore
								setFilters({ ...filters, model: newOption });
								handlePageChange();
							}}
							placeholder="Select a model"
						/>
					</div>

					<div style={{ width: isMobile ? '100%' : '200px' }}>
						<AutoComplete
							//@ts-ignore
							selectedOption={filters?.subCategory}
							options={partOptions?.subCategoryOptions || []}
							label="Sub-Cat"
							onChange={(newOption: Option | null) => {
								setLoading(true);
								//@ts-ignore
								setFilters({ ...filters, subCategory: newOption });
								handlePageChange();
							}}
							placeholder="Select a sub category"
							renderOption={(props: any, item: any) => {
								return (
									<Box
										{...props}
										key={item?.id}
										display={'flex'}
										flexDirection={'column'}
										sx={{
											alignItems: 'flex-start !important',
										}}
									>
										<ListItemText>{item?.value}</ListItemText>
										<Typography sx={{ fontSize: '10px', color: 'gray' }}>
											{item?.category_name}
										</Typography>
									</Box>
								);
							}}
						/>
					</div>
					<div style={{ width: isMobile ? '100%' : '160px' }}>
						<AutoComplete
							//@ts-ignore
							selectedOption={filters?.brand}
							options={partOptions?.brandOptions || []}
							label="Brand"
							onChange={(newOption: Option | null) => {
								setLoading(true);
								//@ts-ignore
								setFilters({ ...filters, brand: newOption });
								handlePageChange();
							}}
							placeholder="Select a brand"
						/>
					</div>
					<div style={{ width: isMobile ? '100%' : '200px' }}>
						<AutoComplete
							//@ts-ignore
							selectedOption={filters?.dimension}
							options={partOptions?.dimensionOptions || []}
							label="Dimension"
							onChange={(newOption: Option | null) => {
								setLoading(true);
								//@ts-ignore
								setFilters({ ...filters, dimension: newOption });
							}}
							placeholder="Select a dimension"
						/>
					</div>
					<div style={{ width: isMobile ? '100%' : '200px' }}>
						<AutoComplete
							//@ts-ignore
							selectedOption={filters?.design}
							options={partOptions?.designOptions || []}
							label="Design"
							onChange={(newOption: Option | null) => {
								setLoading(true);
								//@ts-ignore
								setFilters({ ...filters, design: newOption });
							}}
							placeholder="Select a design"
						/>
					</div>
					<Collapse in={showAllFilters} style={{ width: '100%' }}>
						<div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
							<div style={{ width: isMobile ? '100%' : '200px' }}>
								<AutoComplete
									//@ts-ignore
									selectedOption={filters?.weight}
									options={partOptions?.weightOptions || []}
									label="Weight"
									onChange={(newOption: Option | null) => {
										setLoading(true);
										//@ts-ignore
										setFilters({ ...filters, weight: newOption });
										handlePageChange();
									}}
									placeholder="Select a weight"
								/>
							</div>
							<div style={{ width: isMobile ? '100%' : '200px' }}>
								<AutoComplete
									//@ts-ignore
									selectedOption={filters?.composition}
									onChange={(newOption: Option | null) => {
										setLoading(true);
										//@ts-ignore
										setFilters({ ...filters, composition: newOption });
										handlePageChange();
									}}
									options={partOptions?.compositionOptions || []}
									label="Composition"
									placeholder="Select a composition"
								/>
							</div>
							<div style={{ width: isMobile ? '100%' : '160px' }}>
								<AutoComplete
									//@ts-ignore
									selectedOption={filters?.machineBrand}
									options={partOptions?.machineBrandOptions || []}
									label="Machin Brand"
									onChange={(newOption: Option | null) => {
										setLoading(true);
										//@ts-ignore
										setFilters({ ...filters, machineBrand: newOption });
										handlePageChange();
									}}
									placeholder="Select a Machine brand"
								/>
							</div>
						</div>
					</Collapse>
				</StyledContainer>
				<div
					style={{
						display: 'flex',
						alignItems: 'flex-end',
						justifyContent: 'flex-end',
						marginRight: '16px',
						marginBottom: '10px',
					}}
				>
					<CustomTextButton
						title={showAllFilters ? 'Show fewer filters' : 'Show more filters'}
						onClick={() => setShowAllFilters(!showAllFilters)}
					/>
				</div>
			</div>
		);
	}
	return null;
};

export default CustomFilter;
